import { RoleSwitchItems } from "@/components/RoleSwitchItems";
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar as CalendarIcon,
  LineChart,
  ClipboardList,
  Mail,
  FileText,
  Users,
  FileCheck2,
  UserSquare2,
  Settings as SettingsIcon,
  LogOut,
  RefreshCw,
  BookOpen,
  ListChecks,
  FolderKanban,
  ShoppingBag,
  ShieldCheck,
  CalendarDays,
  FileQuestion,
  LifeBuoy,
  ChevronDown,
  ChevronRight,
  Megaphone,
  Briefcase,
  Building2,
  School,
  MessageSquareText,
  FlaskConical,
  Brain,
  Rocket,
  Star,
  Layers,
  Globe,
  Search,
  Menu as MenuIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { resetMockData } from "@/mock";
import { useAdminDemoMode } from "@/hooks/useAdminDemoMode";
import { Sparkles } from "lucide-react";
import { FeedbackModal } from "@/components/FeedbackModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Master-Prompt Stage 6 (Admin Stage 1): two-business nav + switcher + Platform.
// ON by default (Dirk approved). Set to false to restore the legacy 3-group nav.
const ADMIN_NAV_V2 = true;

// Top-level item used by the legacy fallback nav only.
const TOP_LEVEL_ITEM = { label: "Dashboard", to: "/app", icon: LayoutDashboard, end: true };

// ── Legacy 3-group nav (kept verbatim for ADMIN_NAV_V2 === false rollback) ──
const SIDEBAR_GROUPS_LEGACY = [
  {
    key: "assessments",
    label: "ASSESSMENTS",
    items: [
      { label: "Leads", to: "/app/leads", icon: ClipboardList, badgeKey: "leads" },
      { label: "Customers", to: "/app/customers", icon: Users, badgeKey: "customers" },
      { label: "Email Drafts", to: "/app/email-drafts", icon: Mail, badgeKey: "drafts" },
      { label: "Assessments", to: "/app/assessments", icon: FileText },
      { label: "Assessment Templates", to: "/app/assessment-templates", icon: ListChecks },
      { label: "Tenants", to: "/app/tenants", icon: Users, badgeKey: "unprovisioned" },
    ],
  },
  {
    key: "diagnostic",
    label: "DIAGNOSTIC",
    items: [
      { label: "Campaigns", to: "/app/campaigns", icon: Megaphone, adminOnly: true },
      { label: "Organizations", to: "/app/organizations", icon: Building2, adminOnly: true },
      { label: "School Portfolios", to: "/app/portfolios", icon: Briefcase, adminOnly: true },
      { label: "Schools", to: "/app/schools", icon: School, adminOnly: true },
      { label: "Duplicate Review", to: "/app/school-duplicates", icon: School, adminOnly: true },
      { label: "Onboarding & POs", to: "/app/onboarding", icon: FileText, adminOnly: true },
      { label: "Founding Partners", to: "/app/founding-partners", icon: Star, adminOnly: true },
      { label: "Users & Roles", to: "/app/users", icon: Users, adminOnly: true },
      { label: "Auth Health Check", to: "/app/auth-health", icon: ShieldCheck, adminOnly: true },
      { label: "Licenses", to: "/app/licenses", icon: CalendarDays, adminOnly: true },
      { label: "Terms & Conditions", to: "/app/terms", icon: ShieldCheck, adminOnly: true },
      { label: "Service Enquiries", to: "/app/service-inquiries", icon: LifeBuoy, adminOnly: true },
      { label: "Feedback", to: "/app/feedback", icon: MessageSquareText, adminOnly: true },
      { label: "Emails", to: "/app/emails", icon: Mail, adminOnly: true },
      { label: "Email Templates", to: "/app/email-templates", icon: Mail, adminOnly: true },
      { label: "Attribution", to: "/app/attribution", icon: ShieldCheck, adminOnly: true },
      { label: "Sales Data Health", to: "/app/data-integrity", icon: ShieldCheck, adminOnly: true },
      { label: "Indicator Help Text", to: "/app/help-text", icon: FileQuestion, adminOnly: true },
      { label: "Experts", to: "/app/experts", icon: Users, adminOnly: true },
    ],
  },
  {
    key: "general",
    label: "GENERAL",
    items: [
      { label: "Calendar", to: "/app/calendar", icon: CalendarIcon },
      { label: "Analytics", to: "/app/analytics", icon: LineChart },
      { label: "Scope of Services", to: "/app/scope-of-services", icon: FileCheck2 },
      { label: "Playbooks", to: "/app/playbooks", icon: BookOpen },
      { label: "Project Tracker", to: "/app/project-tracker", icon: FolderKanban },
      { label: "Product Catalog", to: "/app/products", icon: ShoppingBag, badgeKey: "pendingSignups", adminOnly: true },
      { label: "Data Safety", to: "/app/data-safety", icon: ShieldCheck, adminOnly: true },
      { label: "Scenario Lab", to: "/app/scenario-lab", icon: FlaskConical, adminOnly: true },
      { label: "Demo Environments", to: "/app/demos", icon: Rocket, adminOnly: true },
      { label: "Landing Page", to: "/app/settings/landing", icon: Megaphone, adminOnly: true },
      { label: "Sandbox Access", to: "/app/sandbox-access", icon: FlaskConical, adminOnly: true },
      { label: "AI Access", to: "/app/ai-access", icon: Brain, adminOnly: true },
      { label: "Employees", to: "/app/employees", icon: UserSquare2 },
      { label: "Settings", to: "/app/settings", icon: SettingsIcon },
    ],
  },
];

// ── Stage 6 — ADMIN_NAV: two businesses (MySchoolRx | Clarity) + Platform utility ──
const ICON = {
  "Overview": LayoutDashboard,
  "Campaigns": Megaphone,
  "Attribution": ShieldCheck,
  "Sales Data Health": ShieldCheck,
  "Referral Program": Star,
  "Schools": School,
  "Duplicate Review": School,
  "School Portfolios": Briefcase,
  "Organizations": Building2,
  "Licenses": CalendarDays,
  "Pending Signups": ShoppingBag,
  "Product Settings": ShoppingBag,
  "Onboarding & POs": FileText,
  "Founding Partners": Star,
  "Terms & Conditions": ShieldCheck,
  "Data Safety": ShieldCheck,
  "Indicator Help Text": FileQuestion,
  "School Intelligence Access": Brain,
  "Website CMS": Globe,
  "Email Templates": Mail,
  "Service Enquiries": LifeBuoy,
  "Feedback": MessageSquareText,
  "Demo Environments": Rocket,
  "Scenario Lab": FlaskConical,
  "Sandbox Access": FlaskConical,
  "Calendar": CalendarIcon,
  "Analytics": LineChart,
  "Leads": ClipboardList,
  "Email Drafts": Mail,
  "Customers": Users,
  "Tenants": Users,
  "Scope of Services": FileCheck2,
  "Project Tracker": FolderKanban,
  "Playbooks": BookOpen,
  "Assessments": FileText,
  "Assessment Templates": ListChecks,
  "Product Catalog": ShoppingBag,
  "Pricing": CalendarDays,
  "Employees": UserSquare2,
  "Users & Roles": Users,
  "Auth Health Check": ShieldCheck,
  "Emails": Mail,
  "Data Maintenance": SettingsIcon,
  "Release Version": SettingsIcon,
  "Risk Band Migration": Layers,
};
const iconFor = (label) => ICON[label] || FileText;

const ADMIN_NAV = {
  myschoolrx: {
    label: "MySchoolRx",
    home: "/app/myschoolrx",
    groups: [
      { key: "overview", label: "Overview", items: [{ label: "Overview", to: "/app/myschoolrx", end: true }] },
      { key: "sales", label: "Sales & Outreach", items: [
        { label: "Campaigns", to: "/app/myschoolrx/campaigns", adminOnly: true },
        { label: "Attribution", to: "/app/myschoolrx/attribution", adminOnly: true },
        { label: "Sales Data Health", to: "/app/myschoolrx/sales-data-health", adminOnly: true },
        { label: "Referral Program", to: "/app/myschoolrx/referrals", adminOnly: true },
      ] },
      { key: "careers", label: "Careers", items: [
        { label: "Applicants", to: "/app/myschoolrx/careers/applicants", adminOnly: true, superOnly: true, testid: "admin-nav-applicants" },
      ] },
      { key: "schools", label: "Schools & Portfolios", items: [
        { label: "Schools", to: "/app/myschoolrx/schools", adminOnly: true },
        { label: "Duplicate Review", to: "/app/myschoolrx/schools/duplicates", adminOnly: true },
        { label: "School Portfolios", to: "/app/myschoolrx/portfolios", adminOnly: true },
        { label: "Organizations", to: "/app/myschoolrx/organizations", adminOnly: true },
      ] },
      { key: "licensing", label: "Licensing & Onboarding", items: [
        { label: "Licenses", to: "/app/myschoolrx/licenses", adminOnly: true },
        { label: "Pending Signups", to: "/app/myschoolrx/signups", adminOnly: true, badgeKey: "pendingSignups" },
        { label: "Product Settings", to: "/app/myschoolrx/product", adminOnly: true },
        { label: "Onboarding & POs", to: "/app/myschoolrx/onboarding", adminOnly: true },
        { label: "Founding Partners", to: "/app/myschoolrx/founding-partners", adminOnly: true },
        { label: "Terms & Conditions", to: "/app/myschoolrx/terms", adminOnly: true },
        { label: "Data Safety", to: "/app/myschoolrx/data-safety", adminOnly: true },
      ] },
      { key: "content", label: "Diagnostic Content", items: [
        { label: "Indicator Help Text", to: "/app/myschoolrx/help-text", adminOnly: true },
        { label: "School Intelligence Access", to: "/app/myschoolrx/ai-access", adminOnly: true },
        { label: "Scoring Switch", to: "/app/myschoolrx/scoring-switch", adminOnly: true, superOnly: true, testid: "admin-nav-scoring-switch" },
      ] },
      { key: "web", label: "Website & Email", items: [
        { label: "Website CMS", to: "/app/myschoolrx/cms", adminOnly: true },
        { label: "Email Templates", to: "/app/myschoolrx/email-templates", adminOnly: true },
      ] },
      { key: "support", label: "Support", items: [
        { label: "Service Enquiries", to: "/app/myschoolrx/service-enquiries", adminOnly: true },
        { label: "Feedback", to: "/app/myschoolrx/feedback", adminOnly: true },
      ] },
      { key: "demos", label: "Demos & Testing", items: [
        { label: "Demo Environments", to: "/app/myschoolrx/demos", adminOnly: true },
        { label: "Act-as Sessions", to: "/app/myschoolrx/act-as-log", adminOnly: true },
        { label: "Scenario Lab", to: "/app/myschoolrx/scenario-lab", adminOnly: true },
        { label: "Sandbox Access", to: "/app/myschoolrx/sandbox", adminOnly: true },
      ] },
    ],
  },
  clarity: {
    label: "Clarity",
    home: "/app/clarity",
    groups: [
      { key: "overview", label: "Overview", items: [
        { label: "Overview", to: "/app/clarity", end: true },
        { label: "Calendar", to: "/app/clarity/calendar", adminOnly: true },
        { label: "Analytics", to: "/app/clarity/analytics" },
      ] },
      { key: "pipeline", label: "Pipeline", items: [
        { label: "Leads", to: "/app/clarity/leads", badgeKey: "leads" },
        { label: "Email Drafts", to: "/app/clarity/email-drafts", badgeKey: "drafts" },
      ] },
      { key: "delivery", label: "Customers & Delivery", items: [
        { label: "Customers", to: "/app/clarity/customers", badgeKey: "customers" },
        { label: "Tenants", to: "/app/clarity/tenants", badgeKey: "unprovisioned" },
        { label: "Scope of Services", to: "/app/clarity/scope" },
        { label: "Project Tracker", to: "/app/clarity/project-tracker" },
        { label: "Playbooks", to: "/app/clarity/playbooks" },
      ] },
      { key: "assessments", label: "Assessments", items: [
        { label: "Assessments", to: "/app/clarity/assessments" },
        { label: "Assessment Templates", to: "/app/clarity/assessment-templates" },
      ] },
      { key: "products", label: "Products & Pricing", items: [
        { label: "Product Catalog", to: "/app/clarity/products", adminOnly: true },
        { label: "Pricing", to: "/app/clarity/pricing", adminOnly: true },
        { label: "Licenses", to: "/app/clarity/licenses", adminOnly: true },
      ] },
      { key: "web", label: "Website & Email", items: [
        { label: "Website CMS", to: "/app/clarity/cms", adminOnly: true },
        { label: "Email Templates", to: "/app/clarity/email-templates", adminOnly: true },
      ] },
    ],
  },
  platform: {
    label: "Platform",
    items: [
      { label: "Employees", to: "/app/platform/employees" },
      { label: "Users & Roles", to: "/app/platform/users", adminOnly: true },
      { label: "Auth Health Check", to: "/app/platform/auth-health", adminOnly: true },
      { label: "Emails", to: "/app/platform/emails", adminOnly: true },
      { label: "Data Maintenance", to: "/app/platform/data-maintenance", adminOnly: true },
      { label: "Risk Band Migration", to: "/app/platform/risk-bands", adminOnly: true },
      { label: "Release Version", to: "/app/platform/release", adminOnly: true },
    ],
  },
};

const withIcons = (items) => items.map((it) => ({ ...it, icon: iconFor(it.label) }));

// Current business from the path; on /app/platform/* or unknown, use last business.
const bizFromPath = (pathname) => {
  if (pathname.startsWith("/app/clarity")) return "clarity";
  if (pathname.startsWith("/app/myschoolrx")) return "myschoolrx";
  let last = null;
  try { last = localStorage.getItem("admin.lastBusiness"); } catch { /* ignore */ }
  return last === "clarity" ? "clarity" : "myschoolrx";
};

// Breadcrumb: longest matching `to` wins so overview doesn't swallow sub-pages.
const ALL_NAV_ITEMS = (() => {
  const rows = [];
  for (const bizKey of ["myschoolrx", "clarity"]) {
    for (const g of ADMIN_NAV[bizKey].groups) {
      for (const it of g.items) rows.push({ business: ADMIN_NAV[bizKey].label, group: g.label, page: it.label, to: it.to });
    }
  }
  for (const it of ADMIN_NAV.platform.items) rows.push({ business: "Platform", group: null, page: it.label, to: it.to });
  return rows.sort((a, b) => b.to.length - a.to.length);
})();
const buildBreadcrumb = (pathname) =>
  ALL_NAV_ITEMS.find((r) => pathname === r.to || pathname.startsWith(`${r.to}/`)) || null;

const findActiveGroupKey = (groups, pathname) => {
  // longest match first
  let best = null; let bestLen = -1;
  for (const group of groups) {
    for (const it of group.items) {
      if ((pathname === it.to || pathname.startsWith(`${it.to}/`)) && it.to.length > bestLen) {
        best = group.key; bestLen = it.to.length;
      }
    }
  }
  return best;
};

// ── Shared badge + version hooks ──
const useAdminBadges = (session) => {
  const [badges, setBadges] = useState({ unprovisioned: 0, leads: 0, customers: 0, drafts: 0, pendingSignups: 0 });
  useEffect(() => {
    const headers = {
      "x-tenant-id": session?.tenant?.id || "tenant_1",
      "x-user-id": session?.user?.id || "admin",
      "x-user-role": session?.user?.role || "admin",
    };
    fetch(`${BACKEND_URL}/api/tenants/provisioned`, { headers })
      .then((r) => r.json())
      .then((tenants) => {
        const pending = Array.isArray(tenants)
          ? tenants.filter((t) => !t.portal_credentials || !t.portal_credentials.username).length
          : 0;
        setBadges((p) => ({ ...p, unprovisioned: pending }));
      })
      .catch(() => {});

    const CUSTOMER_STAGES = new Set([
      "ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED",
      "SCOPE_CONFIRMED", "SOW_SENT", "SOW_APPROVED",
      "PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY",
      "PROJECT_COMPLETED", "PROJECT_DELIVERED",
      "RETAINER_OFFERED", "RETAINER_ACTIVE", "AT_RISK", "CHURNED",
    ]);
    fetch(`${BACKEND_URL}/api/prospects`, { headers })
      .then((r) => r.json())
      .then((prospects) => {
        const arr = Array.isArray(prospects) ? prospects : [];
        const leads = arr.filter((p) => !CUSTOMER_STAGES.has((p.stage || "").toUpperCase())).length;
        const customers = arr.filter((p) => CUSTOMER_STAGES.has((p.stage || "").toUpperCase())).length;
        setBadges((p) => ({ ...p, leads, customers }));
      })
      .catch(() => {});

    fetch(`${BACKEND_URL}/api/email-drafts`, { headers })
      .then((r) => r.json())
      .then((d) => {
        const count = Array.isArray(d) ? d.length : (d?.drafts?.length || 0);
        setBadges((p) => ({ ...p, drafts: count }));
      })
      .catch(() => {});

    fetch(`${BACKEND_URL}/api/admin/pending-signups`, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const pending = Array.isArray(rows)
          ? rows.filter((s) => s.signup_status === "po_pending_review").length
          : 0;
        setBadges((p) => ({ ...p, pendingSignups: pending }));
      })
      .catch(() => {});
  }, [session]);
  return badges;
};

const useAppVersion = () => {
  const [appVersion, setAppVersion] = useState("");
  useEffect(() => {
    const apply = (d) => setAppVersion(d?.version || "");
    fetch(`${BACKEND_URL}/api/app-version`).then((r) => r.json()).then(apply).catch(() => {});
    const onChange = (e) => { if (e?.detail) setAppVersion(e.detail); };
    window.addEventListener("app-version-changed", onChange);
    return () => window.removeEventListener("app-version-changed", onChange);
  }, []);
  return appVersion;
};

// Desktop top bar — breadcrumb + business chip (Stage 6 · 1.6).
export const AdminTopBar = () => {
  const location = useLocation();
  const crumb = buildBreadcrumb(location.pathname);
  const chip = crumb?.business || (bizFromPath(location.pathname) === "clarity" ? "Clarity" : "MySchoolRx");
  return (
    <div
      className="hidden lg:flex items-center justify-between gap-4 h-12 px-6 lg:px-10 bg-white border-b border-[#e5e7eb] sticky top-0 z-20"
      data-testid="admin-topbar"
    >
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex items-center gap-1.5 text-[12.5px] text-[#6b7280] truncate">
          {crumb ? (
            <>
              <li className="font-semibold text-[#374151]">{crumb.business}</li>
              {crumb.group && (<><li aria-hidden className="text-[#9ca3af]">/</li><li>{crumb.group}</li></>)}
              <li aria-hidden className="text-[#9ca3af]">/</li>
              <li className="text-[#111827] font-semibold">{crumb.page}</li>
            </>
          ) : (
            <li className="text-[#111827] font-semibold">{chip}</li>
          )}
        </ol>
      </nav>
      <span
        className="shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-semibold border"
        style={{ color: "#00698a", backgroundColor: "#e6f4f8", borderColor: "#b3dce7" }}
        data-testid="admin-business-chip"
      >
        {chip}
      </span>
    </div>
  );
};

export const AdminSidebar = ({ session, onSignOut }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const badges = useAdminBadges(session);
  const appVersion = useAppVersion();
  const { theme, setTheme } = useTheme();
  const { eligible: demoEligible, on: demoOn, setOn: setDemoOn } = useAdminDemoMode(session);
  const [fbOpen, setFbOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pinned, setPinned] = useState(() => {
    try {
      const raw = localStorage.getItem("admin-nav-pinned");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  const isAdmin = ["super_admin", "admin"].includes(session?.user?.role);
  const isSuperAdmin = (session?.user?.email || "").toLowerCase() === "dirk@589labs.ai";

  const currentBiz = bizFromPath(location.pathname);
  const onPlatform = location.pathname.startsWith("/app/platform");

  const groups = useMemo(
    () => (ADMIN_NAV_V2 ? ADMIN_NAV[currentBiz].groups.map((g) => ({ ...g, items: withIcons(g.items) })) : SIDEBAR_GROUPS_LEGACY),
    [currentBiz],
  );
  const platformItems = useMemo(() => withIcons(ADMIN_NAV.platform.items), []);

  const togglePin = (to) => {
    setPinned((prev) => {
      const next = prev.includes(to) ? prev.filter((p) => p !== to) : [...prev, to];
      try { localStorage.setItem("admin-nav-pinned", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Persist last business + last path per business on every route change.
  useEffect(() => {
    if (!ADMIN_NAV_V2) return;
    const p = location.pathname;
    let b = null;
    if (p.startsWith("/app/clarity")) b = "clarity";
    else if (p.startsWith("/app/myschoolrx")) b = "myschoolrx";
    if (b) {
      try {
        localStorage.setItem("admin.lastBusiness", b);
        localStorage.setItem(`admin.lastPath.${b}`, p + location.search + location.hash);
      } catch { /* ignore */ }
    }
  }, [location.pathname, location.search, location.hash]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Group collapse state is persisted per business.
  const groupStoreKey = ADMIN_NAV_V2 ? `admin.groups.${currentBiz}` : "admin.groups.legacy";
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(groupStoreKey) || "{}"); } catch { stored = {}; }
    const activeKey = findActiveGroupKey(groups, location.pathname);
    if (activeKey) stored[activeKey] = true;
    // Overview groups default open so the home link is always visible.
    if (stored.overview === undefined) stored.overview = true;
    setExpanded(stored);
  }, [groupStoreKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand the group that contains the active route.
  useEffect(() => {
    const activeKey = findActiveGroupKey(groups, location.pathname);
    if (activeKey) setExpanded((prev) => (prev[activeKey] ? prev : { ...prev, [activeKey]: true }));
  }, [location.pathname, groups]);

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(groupStoreKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Platform disclosure — collapsed by default, auto-expands on platform paths.
  const [platformOpen, setPlatformOpen] = useState(onPlatform);
  useEffect(() => { if (onPlatform) setPlatformOpen(true); }, [onPlatform]);

  const name = session?.user?.full_name || "Consultant";
  const role = session?.user?.role === "admin" ? "Super User" : "Consultant";
  const initials = name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  const switchBiz = (targetBiz) => {
    let last = null;
    try { last = localStorage.getItem(`admin.lastPath.${targetBiz}`); } catch { /* ignore */ }
    navigate(last || ADMIN_NAV[targetBiz].home);
  };

  // Renders a single NavLink row (used by grouped, platform, pinned + search results).
  const renderNavItem = (item, opts = {}) => {
    const Icon = item.icon || iconFor(item.label);
    const count = item.badgeKey ? badges[item.badgeKey] : 0;
    const isPinned = pinned.includes(item.to);
    return (
      <div key={`${opts.ns || "nav"}-${item.to}`} className="group/nav relative">
        <NavLink
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `relative flex items-center gap-3 pl-[18px] pr-9 py-[9px] text-[13px] transition-colors ${
              isActive
                ? "bg-[#e6f4f8] text-[#00698a] font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-[#007a9e]"
                : "text-[#374151] font-medium hover:bg-[#f9fafb] hover:text-[#111827]"
            }`
          }
          aria-current={undefined}
          data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
        >
          <Icon className="h-[15px] w-[15px] shrink-0" />
          <span className="flex-1 truncate">{item.label}</span>
          {count > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] px-[6px] py-[2px] rounded-full text-[11.5px] font-bold leading-none bg-[#007a9e] text-white">
              {count}
            </span>
          )}
        </NavLink>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.to); }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded-md transition-opacity ${
            isPinned ? "opacity-100" : "opacity-0 group-hover/nav:opacity-100 focus:opacity-100"
          }`}
          aria-label={isPinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
          title={isPinned ? "Unpin" : "Pin to top"}
          data-testid={`admin-nav-pin-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
        >
          <Star className="h-[13px] w-[13px]" style={isPinned ? { color: "#007a9e", fill: "#007a9e" } : { color: "#9ca3af" }} />
        </button>
      </div>
    );
  };

  const allVisibleItems = useMemo(() => {
    const arr = [];
    for (const g of groups) for (const it of g.items) if ((!it.adminOnly || isAdmin) && (!it.superOnly || isSuperAdmin)) arr.push(it);
    if (ADMIN_NAV_V2) for (const it of platformItems) if (!it.adminOnly || isAdmin) arr.push(it);
    else arr.unshift(TOP_LEVEL_ITEM);
    return arr;
  }, [groups, platformItems, isAdmin]);
  const q = query.trim().toLowerCase();
  const searchResults = q ? allVisibleItems.filter((it) => it.label.toLowerCase().includes(q)) : [];
  const pinnedItems = pinned.map((to) => allVisibleItems.find((it) => it.to === to)).filter(Boolean);

  const platformVisible = platformItems.filter((it) => !it.adminOnly || isAdmin);

  const panel = (
    <>
      {/* Brand */}
      <Link
        to={ADMIN_NAV_V2 ? ADMIN_NAV[currentBiz].home : "/app"}
        className="flex items-center gap-3 px-5 py-5 shrink-0 border-b border-[#e5e7eb]"
      >
        <div
          className="flex h-11 w-11 items-center justify-center rounded-[10px] font-bold text-[15px] tracking-wide shrink-0"
          style={{ backgroundColor: "#1a1f3c", color: "#e8a020" }}
        >
          589
        </div>
        <div className="leading-tight">
          <div className="text-[16px] font-bold tracking-tight text-[#111827]">
            589 Labs<span style={{ color: "#007a9e" }}>.ai</span>
          </div>
          <div className="text-[11.5px] font-semibold tracking-[0.18em] uppercase text-[#6b7280]">
            Super Admin
          </div>
        </div>
      </Link>

      {/* Business switcher (Stage 6 · 1.3) */}
      {ADMIN_NAV_V2 && (
        <nav aria-label="Business" className="px-3 pt-3" data-testid="admin-business-switcher">
          <div className="grid grid-cols-2 gap-1.5">
            {["myschoolrx", "clarity"].map((b) => {
              const active = currentBiz === b;
              return (
                <a
                  key={b}
                  href={ADMIN_NAV[b].home}
                  onClick={(e) => { e.preventDefault(); switchBiz(b); }}
                  aria-current={active ? "page" : undefined}
                  data-testid={`admin-business-${b}`}
                  className={`h-9 rounded-lg text-[13px] font-semibold flex items-center justify-center border transition-colors ${
                    active
                      ? "bg-[#e6f4f8] text-[#00698a] border-[#b3dce7]"
                      : "bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f9fafb]"
                  }`}
                >
                  {ADMIN_NAV[b].label}
                </a>
              );
            })}
          </div>
        </nav>
      )}

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto py-3">
        {/* Search / quick filter */}
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9ca3af]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search menu…"
              className="w-full h-8 pl-8 pr-2 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] text-[13px] text-[#111827] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#007a9e]/30 focus:border-[#007a9e]"
              data-testid="admin-nav-search"
            />
          </div>
        </div>

        {q ? (
          <div data-testid="admin-nav-search-results">
            {searchResults.length === 0
              ? <div className="px-[18px] py-3 text-[12px] text-[#9ca3af]">No matches</div>
              : searchResults.map((item) => renderNavItem(item, { ns: "search" }))}
          </div>
        ) : (
          <>
            {/* Legacy top-level Dashboard item (fallback nav only) */}
            {!ADMIN_NAV_V2 && (
              <div className="mb-1" data-testid="admin-sidebar-top-level">
                {renderNavItem(TOP_LEVEL_ITEM, { ns: "top" })}
              </div>
            )}

            {/* Pinned favourites */}
            {pinnedItems.length > 0 && (
              <div data-testid="admin-sidebar-pinned">
                <div aria-hidden style={{ height: 1, background: "#e5e7eb", margin: "8px 18px" }} />
                <div className="px-[18px] py-[6px] text-[11.5px] font-bold tracking-[0.12em] uppercase text-[#6b7280]">Pinned</div>
                {pinnedItems.map((item) => renderNavItem(item, { ns: "pin" }))}
              </div>
            )}

            {/* Collapsible groups for the current business */}
            {groups.map((group) => {
              const visibleItems = group.items.filter((item) => (!item.adminOnly || isAdmin) && (!item.superOnly || isSuperAdmin));
              if (visibleItems.length === 0) return null;
              const isOpen = !!expanded[group.key];
              const gid = ADMIN_NAV_V2 ? `${currentBiz}-${group.key}` : group.key;

              return (
                <div key={gid} data-testid={`admin-sidebar-group-${gid}`}>
                  <div aria-hidden style={{ height: 1, background: "#e5e7eb", margin: "8px 18px" }} />
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center justify-between px-[18px] py-[6px] text-[11.5px] font-bold tracking-[0.12em] uppercase text-[#6b7280] transition-colors hover:text-[#111827]"
                    data-testid={`admin-sidebar-group-toggle-${gid}`}
                    aria-expanded={isOpen}
                  >
                    <span>{group.label}</span>
                    {isOpen
                      ? <ChevronDown className="h-3 w-3 text-[#9ca3af]" />
                      : <ChevronRight className="h-3 w-3 text-[#9ca3af]" />
                    }
                  </button>
                  {isOpen && (
                    <div className="mt-1 mb-1" data-testid={`admin-sidebar-group-items-${gid}`}>
                      {visibleItems.map((item) => renderNavItem(item, { ns: "group" }))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Platform utility area (Stage 6 · 1.4) */}
            {ADMIN_NAV_V2 && platformVisible.length > 0 && (
              <div data-testid="admin-sidebar-platform" className="mt-2">
                <div aria-hidden style={{ height: 1, background: "#e5e7eb", margin: "8px 18px" }} />
                <button
                  type="button"
                  onClick={() => setPlatformOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-[18px] py-[6px] text-[11.5px] font-bold tracking-[0.12em] uppercase text-[#9ca3af] transition-colors hover:text-[#111827]"
                  data-testid="admin-sidebar-platform-toggle"
                  aria-expanded={platformOpen}
                >
                  <span>Platform</span>
                  {platformOpen
                    ? <ChevronDown className="h-3 w-3 text-[#9ca3af]" />
                    : <ChevronRight className="h-3 w-3 text-[#9ca3af]" />
                  }
                </button>
                {platformOpen && (
                  <div className="mt-1 mb-1" data-testid="admin-sidebar-platform-items">
                    {platformVisible.map((item) => renderNavItem(item, { ns: "platform" }))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </nav>

      {/* Release version */}
      {appVersion && (
        <div
          className="px-5 py-2 text-[11.5px] font-semibold tracking-wider uppercase text-[#9ca3af] border-t border-[#e5e7eb]"
          data-testid="admin-release-version"
        >
          Release v{appVersion}
        </div>
      )}

      {/* User card */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-3 px-5 py-4 hover:bg-[#f9fafb] transition-colors text-left border-t border-[#e5e7eb]"
            data-testid="admin-user-menu"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white"
              style={{ backgroundColor: "#007a9e" }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-[#111827] truncate">{name}</div>
              <div className="text-[11.5px] text-[#6b7280] truncate">{role}</div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-[290px] max-w-[92vw] max-h-[80vh] overflow-y-auto">
          <DropdownMenuLabel className="text-[11.5px] font-semibold tracking-wider uppercase text-[#6b7280]">
            {role}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <RoleSwitchItems />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            <span className="mr-2 h-4 w-4">{theme === "dark" ? "☀" : "🌙"}</span>
            Toggle theme
          </DropdownMenuItem>
          {demoEligible && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={(e) => { e.preventDefault(); setDemoOn(!demoOn); }}
              data-testid="admin-demo-mode-toggle"
            >
              <Sparkles className={`mr-2 h-4 w-4 ${demoOn ? "text-[#007a9e]" : "text-slate-400"}`} />
              <span className={demoOn ? "text-[#00698a] font-semibold" : ""}>
                Demo Mode: {demoOn ? "ON" : "Off"}
              </span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => { resetMockData(); window.location.reload(); }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset Demo Data
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => setFbOpen(true)}
            data-testid="admin-menu-give-feedback"
          >
            <MessageSquareText className="mr-2 h-4 w-4" />
            Give Us Feedback
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer text-[#dc2626] focus:text-[#dc2626]" onClick={onSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <>
      {/* Mobile top bar (hamburger) — shown below lg */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center gap-3 px-4 h-14 bg-white border-b border-[#e5e7eb]"
        data-testid="admin-mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-[#111827] hover:bg-[#f3f4f6]"
          aria-label="Menu"
          aria-expanded={mobileOpen}
          data-testid="admin-mobile-menu-button"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
        <Link to={ADMIN_NAV_V2 ? ADMIN_NAV[currentBiz].home : "/app"} className="flex items-center gap-2 min-w-0">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-[8px] font-bold text-[12px] shrink-0"
            style={{ backgroundColor: "#1a1f3c", color: "#e8a020" }}
          >
            589
          </div>
          <div className="text-[15px] font-bold tracking-tight text-[#111827] truncate">
            589 Labs<span style={{ color: "#007a9e" }}>.ai</span>
          </div>
        </Link>
      </div>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col fixed top-0 left-0 z-40 h-screen w-[240px] bg-white border-r border-[#e5e7eb]"
        data-testid="admin-sidebar"
      >
        {panel}
      </aside>

      {/* Mobile slide-out drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="w-[260px] max-w-[85vw] p-0 flex flex-col bg-white"
          data-testid="admin-mobile-drawer"
        >
          {panel}
        </SheetContent>
      </Sheet>

      <FeedbackModal open={fbOpen} onOpenChange={setFbOpen} portalType="Super Admin" />
    </>
  );
};
